import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import LogoHeader from '../components/LogoHeader';
import Timer from '../components/Timer';
import QuestionCard from '../components/QuestionCard';
import QuestionPalette from '../components/QuestionPalette';
import SubmitModal from '../components/SubmitModal';
import { 
  generateSessionId, 
  getExamSession, 
  saveExamSession,
  saveExamSessionWithBackend,
  updateExamSession,
  updateExamSessionWithBackend,
  completeExamSession
} from '../utils/examStorage';
import { useAntiCheat } from '../hooks/useAntiCheat';
import { getCurrentUserId } from '../utils/examStorage';

// Branding configuration
const brandConfig = {
  instituteName: "TPI Institute",
  watermarkText: "Powered by TheExamHub",
  watermarkStyle: {
    fontSize: '40px',
    fontFamily: 'Georgia, serif',
    fontWeight: '600',
    letterSpacing: '0.05em',
    opacity: '0.09',
    background: 'linear-gradient(45deg, #B8860B, #DAA520)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  }
};

const ExamPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { requestFullscreen } = useAntiCheat();
  
  // Get user data from Redux store
  const userData = useSelector(state => state.me.data);
  const userId = userData?._id || getCurrentUserId();

  const [currentSection, setCurrentSection] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(7200); // fallback
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [showPalette, setShowPalette] = useState(false);
  const {loading:examDataLoading, data: examData, error:examDataError} = useSelector(state => state.test);
  
  // Session management
  const [sessionId, setSessionId] = useState(null);
  const [isSessionRestored, setIsSessionRestored] = useState(false);
  
  // Debounced sync to prevent multiple rapid API calls
  const [pendingUpdates, setPendingUpdates] = useState({});
  const [syncTimeout, setSyncTimeout] = useState(null);
  const pendingUpdatesRef = useRef({});
  const syncTimeoutRef = useRef(null);
  const isUpdatingTogetherRef = useRef(false); // Use ref for immediate flag access
  const [isUpdatingTogether, setIsUpdatingTogether] = useState(false); // Flag to prevent double calls
  const [isInitialized, setIsInitialized] = useState(false); // Flag to prevent initial API calls

  // Real-time session updates with backend sync
  const updateSessionInDB = useCallback(async (updates) => {
    if (!sessionId) return;
    
    try {
      await updateExamSessionWithBackend(sessionId, {
        ...updates,
        lastUpdated: Date.now()
      });
    } catch (error) {
      console.error('Error updating session:', error);
    }
  }, [sessionId]);

  // Local updates for time-related data (IndexDB only, no API calls)
  const updateLocalSession = useCallback(async (updates) => {
    if (!sessionId) return;
    
    try {
      await updateExamSession(sessionId, {
        ...updates,
        lastUpdated: Date.now()
      });
    } catch (error) {
      console.error('Error updating local session:', error);
    }
  }, [sessionId]);

  // Simple sync function for user actions (no debounce)
  const syncUserActions = useCallback((updates) => {
    if (!sessionId) return;
    
    // Direct API call without debounce
    updateSessionInDB(updates);
  }, [sessionId, updateSessionInDB]);

  // Detailed analytics tracking
  const [questionAnalytics, setQuestionAnalytics] = useState({});
  const [examStartTime, setExamStartTime] = useState(null);
  const [currentQuestionStartTime, setCurrentQuestionStartTime] = useState(null);
  const [totalTimeSpent, setTotalTimeSpent] = useState(0); // Add state for total time spent
  
  // Auto-hide session restored notification after 2 seconds
  useEffect(() => {
    if (isSessionRestored) {
      const timer = setTimeout(() => {
        setIsSessionRestored(false);
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [isSessionRestored]);
  
  const { violationCount } = useAntiCheat({
    maxViolations: 3,
    onMaxViolations: () => {
      alert('Maximum violations reached. Exam will be auto-submitted.');
      handleSubmitExam();
    }
  });

  // Get sections and current section questions
  const sections = examData?.sections || [];
  const currentSectionData = sections[currentSection];
  const questions = currentSectionData?.questions || [];

  const [answers, setAnswers] = useState({});

  // Initialize exam session
  useEffect(() => {
    const initializeExamSession = async () => {
      if (!examData) return;
      
      // Ensure userId is available before creating session
      if (!userId) {
        console.error('UserId not available, cannot create exam session');
        return;
      }
      
      try {
        // Check for existing session
        const existingSession = await getExamSession(examData._id, userId);
        
        if (existingSession) {
          // Restore existing session
          setSessionId(existingSession.sessionId);
          setExamStartTime(existingSession.examStartTime);
          setTimeRemaining(existingSession.timeRemaining);
          setTotalTimeSpent(existingSession.totalTimeSpent || 0); // Restore totalTimeSpent (should be in seconds)
          setAnswers(existingSession.answers || {});
          setQuestionAnalytics(existingSession.questionAnalytics || {});
          // setOptionChangeHistory(existingSession.optionChangeHistory || {}); // Restore option change history
          setCurrentSection(existingSession.currentSection || 0);
          setCurrentQuestion(existingSession.currentQuestion || 0);
          
          console.log('Restored existing exam session:', existingSession.sessionId);
          
          // Set initialization flag for restored session
          setIsInitialized(true);
        } else {
          // Don't create session automatically - wait for user to actually start
          console.log('No existing session found. Session will be created when exam starts.');
          setExamStartTime(Date.now());
          setTimeRemaining(examData.duration * 60);
        }
      } catch (error) {
        console.error('Error checking exam session:', error);
        // Fallback to basic initialization
        setExamStartTime(Date.now());
        setTimeRemaining(examData.duration * 60);
      }
    };

    initializeExamSession();
  }, [examData, userId]);

  // Create session when user actually starts the exam
  const createExamSession = async () => {
    if (!examData || !userId || sessionId) return; // Don't create if already exists
    
    try {
      const newSessionId = generateSessionId(examData._id);
      setSessionId(newSessionId);
      
      // Save initial session with backend sync and userId
      const sessionData = {
        sessionId: newSessionId,
        examId: examData._id,
        examTitle: examData.title,
        examType: examData.type,
        examDuration: examData.duration,
        userId: userId,
        status: 'active',
        questionAnalytics: {},
        currentSection: 0,
        currentQuestion: 0,
        timeRemaining: examData.duration * 60,
        totalTimeSpent: 0, // Initialize totalTimeSpent in seconds
        examStartTime: Date.now(),
        violationCount: 0
      };
      
      await saveExamSessionWithBackend(sessionData);
      
      console.log('New exam session created with userId:', userId, 'Session ID:', newSessionId);
      
      // Set initialization flag after session is created
      setTimeout(() => {
        setIsInitialized(true);
      }, 1000); // Wait 1 second before allowing API calls
    } catch (error) {
      console.error('Error creating exam session:', error);
    }
  };

  // Call createExamSession when component mounts and exam is ready
  useEffect(() => {
    if (examData && userId && !sessionId) {
      // Small delay to ensure component is fully mounted
      const timer = setTimeout(() => {
        createExamSession();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [examData, userId, sessionId]);

  useEffect(() => {
    if (sessionId && (timeRemaining > 0 || totalTimeSpent > 0)) {
      // Only update IndexDB locally, no API calls
      updateLocalSession({ 
        timeRemaining,
        totalTimeSpent
      });
    }
  }, [timeRemaining, totalTimeSpent, sessionId, updateLocalSession]);

  // Combined update for analytics (which now includes answers)
  useEffect(() => {
    if (sessionId && isInitialized && Object.keys(questionAnalytics).length > 0) {
      const hasData = Object.values(questionAnalytics).some(analytics => 
        analytics.visitCount > 0 || analytics.totalChanges > 0 || analytics.answer?.selectedOption !== null
      );
      
      if (hasData) {
        console.log('Making API call with questionAnalytics updates:', questionAnalytics);
        syncUserActions({ questionAnalytics });
      }
    }
  }, [questionAnalytics, sessionId, syncUserActions, isInitialized]);

  // Update session when navigation changes
  useEffect(() => {
    if (sessionId && isInitialized && (currentSection > 0 || currentQuestion > 0)) {
      console.log('Navigation update - currentSection:', currentSection, 'currentQuestion:', currentQuestion);
      syncUserActions({ 
        currentSection, 
        currentQuestion
      });
    }
  }, [currentSection, currentQuestion, sessionId, syncUserActions, isInitialized]);

  // Remove duplicate questionAnalytics sync
  // useEffect(() => {
  //   if (sessionId && Object.keys(questionAnalytics).length > 0) {
  //     updateSessionInDB({ 
  //       questionAnalytics 
  //     });
  //   }
  // }, [questionAnalytics, sessionId, updateSessionInDB]);

  // Track question visit time
  useEffect(() => {
    if (questions[currentQuestion] && sessionId) {
      const questionId = questions[currentQuestion]._id;
      const now = Date.now();
      
      // Update current question start time
      setCurrentQuestionStartTime(now);
      
      // Update analytics for this question
      setQuestionAnalytics(prev => {
        const existing = prev[questionId] || {
          questionId,
          sectionIndex: currentSection,
          sectionName: currentSectionData?.subjectName,
          visitCount: 0,
          totalTimeSpent: 0,
          timeSpentOnRevisits: 0,
          firstVisitTime: null,
          lastVisitTime: null,
          visits: [],
          totalChanges: 0,
          optionChangesHistory: [],
          finalSelection: null,
          firstSelection: null
        };
        
        const updated = {
          ...existing,
          visitCount: existing.visitCount + 1,
          lastVisitTime: now,
          visits: [...existing.visits, {
            visitNumber: existing.visitCount + 1,
            startTime: now,
            endTime: null
          }]
        };
        
        if (!existing.firstVisitTime) {
          updated.firstVisitTime = now;
        }
        
        return {
          ...prev,
          [questionId]: updated
        };
      });
    }
  }, [currentQuestion, currentSection, questions, currentSectionData, sessionId]);

  // Update end time when leaving question
  useEffect(() => {
    return () => {
      if (currentQuestionStartTime && questions[currentQuestion] && sessionId) {
        const questionId = questions[currentQuestion]._id;
        const now = Date.now();
        const timeSpent = now - currentQuestionStartTime;
        
        setQuestionAnalytics(prev => {
          const existing = prev[questionId];
          if (!existing) return prev;
          
          const updatedVisits = existing.visits.map((visit, index) => {
            if (index === existing.visits.length - 1 && !visit.endTime) {
              return { ...visit, endTime: now };
            }
            return visit;
          });
          
          const totalTimeSpent = updatedVisits.reduce((total, visit) => {
            if (visit.endTime) {
              return total + (visit.endTime - visit.startTime);
            }
            return total;
          }, 0);
          
          const timeSpentOnRevisits = updatedVisits.slice(1).reduce((total, visit) => {
            if (visit.endTime) {
              return total + (visit.endTime - visit.startTime);
            }
            return total;
          }, 0);
          
          return {
            ...prev,
            [questionId]: {
              ...existing,
              totalTimeSpent,
              timeSpentOnRevisits,
              visits: updatedVisits
            }
          };
        });
      }
    };
  }, [currentQuestion, currentSection, questions, currentQuestionStartTime, sessionId]);

  useEffect(() => {
    // Timer countdown
    const interval = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          handleTimeUp();
          return 0;
        }
        return prev - 1;
      });
      
      // Update totalTimeSpent in real-time
      if (examStartTime) {
        setTotalTimeSpent(Math.floor((Date.now() - examStartTime) / 1000)); // Convert to seconds
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [examStartTime]);

  const handleTimeUp = () => {
    alert('Time is up! Exam will be auto-submitted.');
    handleSubmitExam();
  };

  const handleSubmitExam = async () => {
    try {
      // Finalize current question analytics
      if (currentQuestionStartTime && questions[currentQuestion]) {
        const questionId = questions[currentQuestion]._id;
        const now = Date.now();
        const timeSpent = now - currentQuestionStartTime;
        
        setQuestionAnalytics(prev => {
          const existing = prev[questionId];
          if (!existing) return prev;
          
          const updatedVisits = existing.visits.map((visit, index) => {
            if (index === existing.visits.length - 1 && !visit.endTime) {
              return { ...visit, endTime: now };
            }
            return visit;
          });
          
          const totalTimeSpent = updatedVisits.reduce((total, visit) => {
            if (visit.endTime) {
              return total + (visit.endTime - visit.startTime);
            }
            return total;
          }, 0);
          
          const timeSpentOnRevisits = updatedVisits.slice(1).reduce((total, visit) => {
            if (visit.endTime) {
              return total + (visit.endTime - visit.startTime);
            }
            return total;
          }, 0);
          
          return {
            ...prev,
            [questionId]: {
              ...existing,
              totalTimeSpent,
              timeSpentOnRevisits,
              visits: updatedVisits
            }
          };
        });
      }

      // Calculate subject performance
      const subjectPerformance = {};
      sections.forEach(section => {
        const sectionQuestions = section.questions;
        const answeredQuestions = sectionQuestions.filter(q => answers[q._id]?.selectedOption !== null).length;
        const correctAnswers = sectionQuestions.filter(q => {
          const answer = answers[q._id];
          return answer?.selectedOption !== null && answer?.isCorrect;
        }).length;
        
        const totalTimeSpent = sectionQuestions.reduce((total, q) => {
          const analytics = questionAnalytics[q._id];
          return total + (analytics?.totalTimeSpent || 0);
        }, 0);
        
        const averageTimeSpent = answeredQuestions > 0 ? totalTimeSpent / answeredQuestions : 0;
        const accuracy = answeredQuestions > 0 ? (correctAnswers / answeredQuestions) * 100 : 0;
        
        subjectPerformance[section.subjectName] = {
          totalQuestions: sectionQuestions.length,
          answeredQuestions,
          correctAnswers,
          averageTimeSpent,
          accuracy: Math.round(accuracy * 100) / 100 // Round to 2 decimal places
        };
      });

      // Prepare detailed result data with new structure
      const detailedResult = {
        examId: examData?._id,
        examTitle: examData?.title,
        examType: examData?.type,
        examDuration: examData?.duration,
        testSeriesId: examData?.testSeriesId || null, // Test series ID if applicable
        testId: examData?.testId || null, // Test ID if applicable
        startTime: examStartTime,
        endTime: Date.now(),
        totalTimeSpent: totalTimeSpent, // Use real-time totalTimeSpent instead of calculating
        questionAnalytics: questionAnalytics, // Now contains answers within analytics
        subjectPerformance,
        overallFeedback: null, // Will be filled when user provides feedback
        questionFeedbackReportId: null, // Will be generated when user provides question feedback
        violationCount: violationCount,
        timestamp: new Date().toISOString()
      };

      // Complete session and store final result
      if (sessionId) {
        await completeExamSession(sessionId, detailedResult);
        console.log('Exam completed and stored:', detailedResult);
      }

      navigate('/result');
      
    } catch (error) {
      console.error('Error completing exam:', error);
      // Still navigate to result page even if storage fails
      navigate('/result');
    }
  };

  const handleAnswerSelect = (questionId, optionPrompt) => {
    console.log('handleAnswerSelect called:', { questionId, optionPrompt });
    
    // Track option change
    const now = Date.now();
    
    // Use React's batching to update both states together
    React.startTransition(() => {
      setAnswers(prev => {
        const newAnswers = {
          ...prev,
          [questionId]: {
            ...prev[questionId],
            selectedOption: optionPrompt, // Now storing prompt value (1, 2, 3, 4...) instead of index
            isMarked: prev[questionId]?.isMarked || false
          }
        };
        
        // Update analytics in the same transition with answer included
        setQuestionAnalytics(prevAnalytics => {
          const existing = prevAnalytics[questionId] || {
            questionId,
            totalChanges: 0,
            optionChangesHistory: [],
            finalSelection: null,
            firstSelection: null,
            answer: {
              selectedOption: null
            },
            isMarked: false
          };
          
          const newChange = {
            timestamp: now,
            selectedOption: optionPrompt,
            timeSpent: currentQuestionStartTime ? now - currentQuestionStartTime : 0
          };
          
          return {
            ...prevAnalytics,
            [questionId]: {
              ...existing,
              totalChanges: existing.totalChanges + 1,
              optionChangesHistory: [...existing.optionChangesHistory, newChange],
              finalSelection: optionPrompt,
              firstSelection: existing.firstSelection || optionPrompt,
              answer: {
                selectedOption: optionPrompt
              },
              isMarked: existing.isMarked || false
            }
          };
        });
        
        console.log('Updated answers:', newAnswers);
        return newAnswers;
      });
    });
  };

  const handleMarkForReview = (questionId) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        selectedOption: prev[questionId]?.selectedOption || null,
        isMarked: !prev[questionId]?.isMarked
      }
    }));
    
    // Also update analytics with mark status
    setQuestionAnalytics(prevAnalytics => {
      const existing = prevAnalytics[questionId];
      if (!existing) return prevAnalytics;
      
      return {
        ...prevAnalytics,
        [questionId]: {
          ...existing,
          answer: {
            ...existing.answer,
            selectedOption: existing.answer?.selectedOption || null
          },
          isMarked: !existing.isMarked
        }
      };
    });
  };

  const nextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const previousQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const switchToSection = (sectionIndex) => {
    setCurrentSection(sectionIndex);
    setCurrentQuestion(0); // Reset to first question of new section
  };

  // Show error state if exam data failed to load
  if (examDataError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Exam</h2>
          <p className="text-gray-600 mb-4">{examDataError}</p>
          <button 
            onClick={() => navigate('/start')}
            className="btn-primary"
          >
            Back to Start
          </button>
        </div>
      </div>
    );
  }

  // Show error when no exam data is available (direct access to /exam)
  if (!examData && !examDataLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Exam Data Available</h2>
          <p className="text-gray-600 mb-4">Please start an exam from the home page first.</p>
          <button 
            onClick={() => navigate('/start')}
            className="btn-primary"
          >
            Go to Start Page
          </button>
        </div>
      </div>
    );
  }

  // Show loading state while data is being fetched
  if (!examData || examDataLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading exam...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 exam-mode relative overflow-hidden">
      {/* Background Watermark Grid */}
      <div className="absolute inset-0 pointer-events-none select-none">
        <div className="absolute top-0 left-0 w-[200%] h-[200%] -translate-x-1/4 -translate-y-1/4">
          <div className="grid grid-cols-8 gap-2 transform -rotate-12">
            {Array(64).fill(null).map((_, index) => (
              <div key={index} className="flex flex-col gap-2">
                {Array(12).fill(brandConfig.instituteName).map((_, innerIndex) => (
                  <div 
                    key={`${index}-${innerIndex}`} 
                    className="whitespace-nowrap text-center"
                    style={{ 
                      ...brandConfig.watermarkStyle,
                      fontSize: brandConfig.watermarkStyle.fontSize,
                    }}
                  >
                    {brandConfig.instituteName}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Session Restored Notification */}
      {isSessionRestored && (
        <div className="fixed top-0 left-0 w-full bg-green-100 text-green-700 text-center py-2 z-50 text-sm transition-opacity duration-500">
          ✓ Exam session restored - Your previous progress has been loaded
        </div>
      )}

      {/* Header */}
      <header className="bg-white shadow-sm border-b px-4 py-3 relative z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img src="https://theexamhub.com/assets/images/logolatest.png" alt="Logo" className="w-10 h-10 rounded" />
            <div className="text-sm text-gray-700 font-semibold leading-tight">
              Organised by <span className="text-blue-700 font-bold">{brandConfig.instituteName}</span>
            </div>
          </div>
          
          <div className="flex-1 text-center">
            <span className="text-lg font-bold text-gray-900">{examData?.title || 'Exam'}</span>
          </div>
          
          <div className="flex items-center space-x-4">
            <Timer 
              timeRemaining={timeRemaining}
              onTimeUp={handleTimeUp}
            />
            <button
              onClick={() => setShowPalette(!showPalette)}
              className="btn-secondary"
            >
              Question Palette
            </button>
            <button
              onClick={() => setShowSubmitModal(true)}
              className="btn-danger"
            >
              Submit
            </button>
          </div>
        </div>
        
        {violationCount > 0 && (
          <div className="mt-2 text-sm text-red-600">
            Warning: {violationCount} violation(s) detected. 
            {3 - violationCount} more will result in auto-submission.
          </div>
        )}
      </header>

      {/* Section Tabs */}
      {sections.length > 1 && (
        <nav className="bg-gray-100 border-b px-6 py-2 flex space-x-2 relative z-10">
          {sections.map((section, index) => (
            <button
              key={section.subjectName}
              onClick={() => switchToSection(index)}
              className={`px-4 py-2 rounded-t font-medium text-sm border-b-2 transition-colors ${
                currentSection === index
                  ? 'border-blue-600 text-blue-700 bg-white'
                  : 'border-transparent text-gray-600 hover:text-blue-700 hover:bg-white'
              }`}
            >
              {section.subjectName}
            </button>
          ))}
        </nav>
      )}

      <div className="flex relative z-10">
        {/* Main Content */}
        <div className="flex-1 p-6">
          {/* Section Name and Question Info */}
          <div className="flex items-center justify-between mb-4">
            <div className="text-base font-semibold text-blue-700">
              {currentSectionData?.subjectName}
            </div>
            <div className="text-sm text-gray-700">
              Question {currentQuestion + 1} of {questions.length}
            </div>
          </div>

          {/* Question Card */}
          {questions[currentQuestion] && (
            <div>
              <div className="mb-2 text-gray-800 font-bold text-lg">
                Q. {currentQuestion + 1}
              </div>
              <QuestionCard
                question={questions[currentQuestion]}
                selectedOption={answers[questions[currentQuestion]._id]?.selectedOption !== undefined ? answers[questions[currentQuestion]._id]?.selectedOption : null}
                isMarked={answers[questions[currentQuestion]._id]?.isMarked || false}
                onAnswerSelect={(optionIndex) => handleAnswerSelect(questions[currentQuestion]._id, optionIndex)}
                onMarkForReview={() => handleMarkForReview(questions[currentQuestion]._id)}
              />
            </div>
          )}
          
          {/* Navigation */}
          <div className="flex justify-between mt-8">
            <button
              onClick={previousQuestion}
              disabled={currentQuestion === 0}
              className="btn-secondary"
            >
              Previous
            </button>
            
            <button
              onClick={nextQuestion}
              disabled={currentQuestion === questions.length - 1}
              className="btn-primary"
            >
              Next
            </button>
          </div>
        </div>

        {/* Question Palette Sidebar */}
        {showPalette && (
          <div className="w-80 bg-white border-l shadow-lg relative z-10">
            <QuestionPalette
              questions={questions}
              answers={answers}
              currentQuestion={currentQuestion}
              onQuestionSelect={setCurrentQuestion}
              onClose={() => setShowPalette(false)}
            />
          </div>
        )}
      </div>

      {/* Submit Modal */}
      {showSubmitModal && (
        <div className="relative z-20">
          <SubmitModal
            onConfirm={handleSubmitExam}
            onCancel={() => setShowSubmitModal(false)}
            answeredCount={Object.keys(answers).filter(id => answers[id]?.selectedOption !== null).length}
            totalQuestions={questions.length}
          />
        </div>
      )}

      {/* Bottom Watermark */}
      <div className="fixed bottom-0 right-0 p-2 text-gray-300 text-xs opacity-50 select-none pointer-events-none">
        {brandConfig.watermarkText}
      </div>
    </div>
  );
};

export default ExamPage; 